import { UnassignedItem } from 'src/entities/dispatch';

import { LaneGroup } from './laneGroups';
import { SameLocationInfo } from './sameLocationVM';

/**
 * 「同一地点で割当済」タグ (log_web_issues#318) が付いたものを先頭へ寄せる
 * (log_web_issues#332)。**並び順だけを変える。絞り込みではないので件数は変わらない。**
 *
 * Why not 既存の並び替えに混ぜる: タグの判定材料 (`sameLocationInfos`) は D&D ごとに
 * 作り直されるので、検索・フィルタ・ソートやレーンのグルーピングと同じ memo に置くと
 * それらまで毎回巻き込んで再計算することになる。ここは並べ替えるだけの後段に分ける。
 */

/**
 * 「ヒット → 残り」の 2 群に分ける。各群の中は入力順のまま。
 *
 * ヒットが無ければ入力配列をそのまま返す。呼び出し側の memo 2 か所に短絡を
 * 散らさないための短絡なので、関数の中に置く。
 *
 * なお**ヒットが 1 件でもあれば新しい配列を返す**（全件ヒットでも、すでに
 * 「ヒット → 残り」に並んでいても同じ）。返り値の消費者は JSX だけで、この配列を
 * dep に取る memo が無いため、並べ替えが要るかの検出は入れない。
 */
function partition<T>(list: readonly T[], isHit: (x: T) => boolean): readonly T[] {
  const hit = list.filter(isHit);
  if (hit.length === 0) return list;
  return [...hit, ...list.filter((x) => !isHit(x))];
}

/** 伝票単位。タグ付きの案件を先頭へ */
export function prioritizeItems(
  items: readonly UnassignedItem[],
  sameLocationInfos: ReadonlyMap<string, SameLocationInfo>,
): readonly UnassignedItem[] {
  return partition(items, (item) => sameLocationInfos.has(item.id));
}

/**
 * 地点単位。タグ付き案件を **1 件以上**含むレーンを先頭へ。
 *
 * Why not every: レーンは laneId → 両端の座標 → 両端の住所 → 案件 id の順にキーを落として
 * まとめ、タグの判定キーも座標ベースなので配下はほぼ一様にヒットする
 * （laneId でまとまるのは同じ便の伝票なので、やはり両端が同じ）。
 * それでも some にするのは、将来キーの取り方が変わって
 * 一様でなくなったときに「タグが 1 件でも付いているレーンを見せない」ほうが要望から外れるため。
 *
 * Why not 伝票側の partition でレーン順が決まるようにする: `sortLaneGroups` がグループを
 * 合計値で並べ直すので、item 側で先頭へ寄せてもレーン順には残らない。
 */
export function prioritizeLaneGroups(
  groups: readonly LaneGroup[],
  sameLocationInfos: ReadonlyMap<string, SameLocationInfo>,
): readonly LaneGroup[] {
  return partition(groups, (group) => group.items.some((item) => sameLocationInfos.has(item.id)));
}
